import type { Flow } from '~/models/flow.model';
import type { Page } from '~/models/page.model';
import type {
    Allowed,
    LimitationPredicate,
    MandatoryContextProps,
    PossibleLimitations
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import {
    AsAllowed,
    BlockLike,
    LimitationsKeyType,
    PossibleRestrictions
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import {
    forbidClientPaymentPlanInLeadForm,
    forbidInvoiceBeforeServiceSelection,
    forbidTwoInvoiceBlocks
} from '~/components/Builder/Common/CreationLimitations/invoiceLimitations';
import { forbidTwoSchedulerBlocks } from '~/components/Builder/Common/CreationLimitations/schedulerLimitations';
import {
    forbidServiceBlockAfterPagesWithDependentBlocks,
    forbidServiceBlockInFlowWithPaidInvoice,
    forbidTwoSessionSelectionBlocks
} from '~/components/Builder/Common/CreationLimitations/serviceLimitations';
import { alwaysForbidAddingChargeBlock } from '~/components/Builder/Common/CreationLimitations/chargeLimitations';
import {
    forbidContractBlockInPublicFlow,
    forbidContractWithRecapsBeforeServiceSelection
} from '~/components/Builder/Common/CreationLimitations/contractLimitations';
import { UnionToIntersection } from 'type-fest';
import {
    chargeBlockCanOnlyBeOnLastPageInPublicFile,
    forbidMoreThanOneActionBlockInPage
} from './commonActionBlockLimitation';
import { TCreatableActionBlocks } from '~/types/model.types';
import {
    forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock,
    forbidMoreThanMaxUploadQuestionBlocksForSingleBlock
} from '~/components/Builder/Common/CreationLimitations/questionLimitations';
import { forbidSmartFieldsInPublicFlows } from '~/components/Builder/Common/CreationLimitations/textLimitations';
import { forbidCanvablockOnIntegrationGateDisabled } from '~/components/Builder/Common/CreationLimitations/image-limitations';

export const ACTION_BLOCKS_WITH_RULES_TO_DESCRIBE: Record<
    TCreatableActionBlocks,
    {
        name: string;
        newPageName: string;
    }
> = {
    invoice_container_block: {
        name: 'invoice block',
        newPageName: 'Invoice'
    },
    service_selection_block: {
        name: 'service block',
        newPageName: 'Services'
    },
    charge_container_block: {
        name: 'pay',
        newPageName: undefined
    },
    contract_block: {
        name: 'contract block',
        newPageName: 'Contract'
    },
    schedule_container_block: {
        name: 'scheduler block',
        newPageName: 'Scheduler'
    }
};

function runPredicates<
    K extends LimitationsKeyType,
    T extends Partial<Record<K, ReadonlyArray<LimitationPredicate>>>
>(
    predicates: T[keyof T],
    props: UnionToIntersection<Parameters<T[keyof T][number]>[0]> &
        MandatoryContextProps
) {
    if (!predicates) {
        return AsAllowed;
    }

    for (const predicate of predicates) {
        const limitation = predicate(props);
        if (limitation.status !== 'allowed') {
            return limitation;
        }
    }

    return AsAllowed;
}

export function checkLimitations<
    K extends LimitationsKeyType,
    T extends Partial<
        Record<K | LimitationsKeyType, ReadonlyArray<LimitationPredicate>>
    >
>(
    objectOfLimitations: Readonly<T>,
    props: UnionToIntersection<Parameters<T[keyof T][number]>[0]> &
        MandatoryContextProps
): ReturnType<T[keyof T][number]> | Allowed {
    if (objectOfLimitations.all) {
        const limitation = runPredicates(objectOfLimitations.all, props);

        if (limitation.status !== 'allowed') {
            // @ts-ignore TODO Nirel: Need to manage to find the right return value that satisfy LimitationPredicate and the different restrictions.
            return limitation;
        }
    }

    const { sourceBlock } = props;

    if (sourceBlock?.type === 'container_block' && sourceBlock?.childBlocks) {
        for (const childBlock of sourceBlock.childBlocks) {
            const limitation = checkLimitations(objectOfLimitations, {
                ...props,
                sourceBlock: childBlock as BlockLike
            });

            if (limitation.status !== 'allowed') {
                // @ts-ignore TODO Nirel: Need to manage to find the right return value that satisfy LimitationPredicate and the different restrictions.
                return limitation;
            }
        }
    }

    // @ts-ignore TODO Nirel: Need to manage to find the right return value that satisfy LimitationPredicate and the different restrictions.
    return runPredicates(objectOfLimitations[props.sourceBlock?.type], props);
}

export const reuseBlockCreationLimitations = {
    // When you are adding data to an existing block there can only be currently one restriction
    contract_block: [forbidContractWithRecapsBeforeServiceSelection],
    service_selection_block: [
        forbidTwoSessionSelectionBlocks,
        forbidServiceBlockAfterPagesWithDependentBlocks
    ],
    question_block: [forbidMoreThanMaxUploadQuestionBlocksForSingleBlock],
    container_block: [forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock],
    invoice_container_block: [forbidClientPaymentPlanInLeadForm]
} as const;

export const byBlockTypeLimitations = {
    text_block: [forbidSmartFieldsInPublicFlows],
    invoice_container_block: [
        forbidTwoInvoiceBlocks,
        forbidClientPaymentPlanInLeadForm
    ],
    schedule_container_block: [forbidTwoSchedulerBlocks],
    service_selection_block: [
        forbidTwoSessionSelectionBlocks,
        forbidServiceBlockInFlowWithPaidInvoice,
        forbidServiceBlockAfterPagesWithDependentBlocks
    ],
    charge_container_block: [alwaysForbidAddingChargeBlock],
    contract_block: [forbidContractBlockInPublicFlow],
    question_block: [forbidMoreThanMaxUploadQuestionBlocksForSingleBlock],
    container_block: [forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock],
    image_block: [forbidCanvablockOnIntegrationGateDisabled]
} as const;

export const blankBlockCreationLimitations = {
    invoice_container_block: [
        forbidTwoInvoiceBlocks,
        forbidInvoiceBeforeServiceSelection,
        forbidMoreThanOneActionBlockInPage
    ],
    schedule_container_block: [
        forbidTwoSchedulerBlocks,
        forbidMoreThanOneActionBlockInPage
    ],
    service_selection_block: [
        forbidTwoSessionSelectionBlocks,
        forbidMoreThanOneActionBlockInPage,
        forbidServiceBlockInFlowWithPaidInvoice,
        forbidServiceBlockAfterPagesWithDependentBlocks
    ],
    charge_container_block: [
        alwaysForbidAddingChargeBlock,
        forbidMoreThanOneActionBlockInPage
    ],
    contract_block: [
        forbidContractBlockInPublicFlow,
        forbidMoreThanOneActionBlockInPage
    ],
    question_block: [forbidMoreThanMaxUploadQuestionBlocksForSingleBlock],
    container_block: [forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock]
} as const;

export const changeBlockLayoutLimitations = {
    question_block: [forbidMoreThanMaxUploadQuestionBlocksForSingleBlock],
    container_block: [forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock]
};

export const notBlankBlockCreationLimitations = {
    ...blankBlockCreationLimitations,
    contract_block: [
        ...blankBlockCreationLimitations.contract_block,
        forbidContractWithRecapsBeforeServiceSelection
    ],
    all: [chargeBlockCanOnlyBeOnLastPageInPublicFile]
} as const;

export function checkNewPageWithBlocksCreationLimitations({
    flow,
    pagesToAdd
}: {
    flow: Flow;
    pagesToAdd: Page[];
}) {
    return pagesToAdd.reduce<
        Record<
            string,
            PossibleLimitations<
                typeof notBlankBlockCreationLimitations,
                keyof typeof notBlankBlockCreationLimitations
            >
        >
    >((result, pageToAdd) => {
        result[pageToAdd.page_id] = checkLimitations(
            notBlankBlockCreationLimitations,
            {
                sourceBlock: pageToAdd.rootBlock,
                targetFlow: flow,
                targetPage: null,
                targetBlock: pageToAdd.rootBlock as BlockLike
            }
        );

        return result;
    }, {});
}

export function checkNewPageWithBlocksCreationRestrictions(props: {
    flow: Flow;
    pagesToAdd: Page[];
}) {
    const creationLimitationsRestrictionsByPage =
        checkNewPageWithBlocksCreationLimitations(props);

    // We want only the restricted values
    props.pagesToAdd.forEach(page => {
        if (
            creationLimitationsRestrictionsByPage[page.page_id].status ===
            'restricted'
        ) {
            return;
        }

        creationLimitationsRestrictionsByPage[page.page_id] = undefined;
    });

    return creationLimitationsRestrictionsByPage as Record<
        string,
        PossibleRestrictions<
            typeof notBlankBlockCreationLimitations,
            keyof typeof notBlankBlockCreationLimitations
        >
    >;
}
